import React, { useEffect, useState, useCallback } from "react";
import type { Placement } from "@floating-ui/react";
import { openManagementModesEnum, SeemPopover } from "@SeemplicityDev/shared";
import EntityLogo from "./EntityLogo";
import "./EntityList.scss";
import { capitalize } from "lodash";

export interface EntityItem {
  logo?: string | null;
  name?: string;
  displayName?: string;
  count?: number;
  url?: string;
  [key: string]: any;
}

interface EntityListProps {
  list: EntityItem[];
  onEntityClick?: (entity: EntityItem) => void;
  maxToDisplay?: number | null;
  tooltip?: (entity: EntityItem) => string;
  className?: string;
  showTitles?: boolean;
  noGap?: boolean;
  size?: "s" | "m" | "l";
  displayExtended?: boolean;
}

const EntityList: React.FC<EntityListProps> = ({
  list,
  onEntityClick,
  maxToDisplay,
  tooltip,
  className,
  size = "m",
  showTitles = false,
  noGap = false,
  displayExtended = true,
}) => {
  const [displayedData, setDisplayedData] = useState<EntityItem[]>([]);
  const [extendedData, setExtendedData] = useState<EntityItem[]>([]);

  useEffect(() => {
    if (!list) {
      return;
    }

    const newData = [...list]?.sort((a, b) => {
      const countA = a.count ?? 0;
      const countB = b.count ?? 0;
      return countB - countA;
    });

    let extendedData: EntityItem[] = [];
    if (!!maxToDisplay) {
      extendedData = newData?.splice(maxToDisplay);
      setExtendedData(extendedData);
    }
    setDisplayedData(newData);
  }, [list, maxToDisplay]);

  // Function to format tooltip text with proper capitalization
  const formatTooltip = useCallback(
    (entity: EntityItem): string => {
      if (!tooltip) {
        return capitalize(entity.displayName || entity.name || "");
      }
      return tooltip(entity) || "";
    },
    [tooltip]
  );

  return (
    <ul className={`entity-list ${noGap ? "no-gap" : ""} ${className || ""}`}>
      {displayedData?.map((entity, index) => {
        const { url, displayName, name, logo } = entity;
        const entityName = displayName || name;

        return (
          <li key={index} className="entity-item">
            <EntityLogo
              logo={logo}
              name={entityName}
              tooltip={formatTooltip(entity)}
              onClick={
                !!onEntityClick
                  ? (e) => {
                      if (!url) {
                        e.stopPropagation();
                        onEntityClick(entity);
                      }
                    }
                  : null
              }
              isFilterAction
              size={size}
            />
          </li>
        );
      })}
      {!!extendedData?.length && (
        <SeemPopover
          openManagementMode={
            displayExtended ? openManagementModesEnum.HOVER : undefined
          }
          delayClose={300}
          // "top-center" is a valid runtime placement string for this popover but
          // is not part of floating-ui's Placement union; cast to preserve behavior.
          placement={"top-center" as Placement}
          triggerClassName={"expanded-logos-trigger"}
          className={"expanded-logos-popover"}
          body={
            <ul className="expanded-logos-list">
              {extendedData.map((entity, index) => {
                const { displayName, name, count, logo, url } = entity;
                const entityName = displayName || name;

                return (
                  <li key={index} className={"expanded-list-item"}>
                    <EntityLogo
                      logo={logo}
                      name={entityName}
                      tooltip={formatTooltip(entity)}
                      {...(onEntityClick
                        ? {
                            onClick: (e: React.MouseEvent) => {
                              if (!url) {
                                e.stopPropagation();
                                onEntityClick(entity);
                              }
                            },
                          }
                        : {})}
                      isFilterAction
                      size={size}
                    />
                    {showTitles && entityName && (
                      <div className="title">
                        {entityName}
                        {count ? `: ${count}` : ""}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          }
        >
          <span
            className={`more-logos-number ${
              !displayExtended ? "is-disabled" : ""
            }`}
          >{`+${extendedData.length}`}</span>
        </SeemPopover>
      )}
    </ul>
  );
};

export default EntityList;
